import { memo, useEffect, useLayoutEffect, useRef, useState } from "react";
import { Box, Stack, Typography } from "@mui/material";
import { useLoginState } from "../../studioxr/auth/LoginState";
import {
  onAnalyticsInitialized,
  trackEvent,
  trackEventGA,
} from "../../libs/analyticsManager";
import {
  SKETCHUP_EXTENSION_WAREHOUSE_URL,
  readLocalDismissed,
  shouldShowSketchupCard,
  useSketchupPromoStore,
} from "./sketchupPromoGate";
import { Icon, SketchupLogoPair } from "./sketchupPromoParts";

// Clears the fixed "Watch Mattoboard Tutorial" button below the card
// (LibraryOnboardingVideo: bottom 16px + ~36px tall).
const TUTORIAL_CLEARANCE = 48;
// Card heights measured 2026-10-08 (full = with the logo tile), plus the
// clearance and a little air.
const FULL_SPACE = 144 + TUTORIAL_CLEARANCE + 8;
const COMPACT_SPACE = 78 + TUTORIAL_CLEARANCE + 8;

/** Which card fits in `space` px under the nav: the full card, the compact
 *  one without the logo tile, or none (it would clip under the button). */
export const sketchupCardSize = (space: number) =>
  space >= FULL_SPACE ? "full" : space >= COMPACT_SPACE ? "compact" : null;

/**
 * Permanent bottom-left SketchUp extension promo in the workspace sidebar.
 * Appears only after the /home/library banner (SketchupPromoBanner) has been
 * dismissed, then stays for good — no close control. HomeLayout/HomeSideBar
 * own the placement (desktop, expanded rail); this component owns the
 * audience gate and sizes itself to the height the nav leaves free, so an
 * extra nav row (Team, Showrooms) shrinks or hides it instead of overlapping
 * the tutorial button. Memoised: it takes no props, and the sidebar
 * re-renders on every navigation and concept-list update.
 */
export const SketchupPromoCard = memo(function SketchupPromoCard() {
  const profile = useLoginState((state) => state.profile);
  const dismissedUid = useSketchupPromoStore((state) => state.dismissedUid);
  const slotRef = useRef<HTMLDivElement>(null);
  const [space, setSpace] = useState(0);

  const audience = shouldShowSketchupCard({
    profile: profile as any,
    bannerDismissed:
      (!!profile && dismissedUid === profile.uid) ||
      readLocalDismissed(profile?.uid),
  });
  const size = audience ? sketchupCardSize(space) : null;
  const compact = size === "compact";

  useLayoutEffect(() => {
    const slot = slotRef.current;
    if (!slot) return;
    const observer = new ResizeObserver(([entry]) =>
      setSpace(entry.contentRect.height),
    );
    observer.observe(slot);
    return () => observer.disconnect();
  }, [audience]);

  const uid = profile?.uid;
  useEffect(() => {
    if (!size || !uid) return;
    // trackEvent drops events sent before analytics init (geo lookup, EU
    // consent), so mark the uid viewed only once the event can land.
    return onAnalyticsInitialized(() => {
      const store = useSketchupPromoStore.getState();
      if (store.cardViewedUid === uid) return;
      store.markCardViewed(uid);
      trackEvent("Home - SketchUp Card Viewed", {});
    });
  }, [size, uid]);

  if (!audience) return null;

  return (
    // Takes all the height the nav leaves free (the navs' own flexGrow: 1
    // would otherwise split it), and the card sits at its bottom.
    <Box
      ref={slotRef}
      sx={{
        flex: "1000 1 0",
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-end",
      }}
    >
      {size && (
        <Box
          component="a"
          href={SKETCHUP_EXTENSION_WAREHOUSE_URL}
          target="_blank"
          rel="noopener noreferrer"
          aria-label="Try MattoBoard in SketchUp"
          data-testid="sketchup-promo-card"
          data-compact={compact ? "true" : undefined}
          onClick={() => {
            trackEvent("Home - SketchUp Card CTA Click", {});
            // GA4: one event for every "Try MattoBoard in SketchUp" click,
            // with the placement so the banner and the card can be split.
            trackEventGA("sketchup_cta_click", {
              placement: "sidebar_card",
              compact,
            });
          }}
          sx={{
            display: "block",
            mb: `${TUTORIAL_CLEARANCE}px`,
            p: "6px",
            pb: compact ? "8px" : "10px",
            // Clips to the rail while it animates open from 64px.
            overflow: "hidden",
            textDecoration: "none",
            color: "inherit",
            backgroundColor: "#fff",
            border: "1px solid #E7E5E4",
            borderRadius: "12px",
            boxShadow: "0 2px 8px rgba(15,23,42,0.06)",
            transition: "box-shadow 150ms, border-color 150ms",
            "&:hover": {
              borderColor: "#D6D3D1",
              boxShadow: "0 4px 14px rgba(91,71,245,0.14)",
            },
          }}
        >
          {!compact && (
            <Stack
              alignItems="center"
              justifyContent="center"
              sx={{
                height: 60,
                borderRadius: "8px",
                background:
                  "linear-gradient(135deg, #F3F0FF 0%, #FAF8FF 55%, #F1EDFF 100%)",
              }}
            >
              <SketchupLogoPair />
            </Stack>
          )}
          <Typography
            sx={{
              px: "6px",
              pt: compact ? "4px" : "8px",
              fontSize: 12,
              fontWeight: 600,
              lineHeight: 1.3,
              letterSpacing: "-0.2px",
              color: "#18181B",
              whiteSpace: "nowrap",
            }}
          >
            Try MattoBoard in SketchUp
          </Typography>
          <Stack direction="row" alignItems="flex-end" sx={{ px: "6px" }}>
            <Typography
              sx={{
                flexGrow: 1,
                pt: "3px",
                pr: "8px",
                fontSize: 9.5,
                lineHeight: 1.4,
                color: "#737373",
              }}
            >
              Explore, apply, and organise real materials directly inside your
              SketchUp model
            </Typography>
            <Stack
              alignItems="center"
              justifyContent="center"
              aria-hidden
              sx={{
                width: 28,
                height: 28,
                flexShrink: 0,
                borderRadius: "50%",
                backgroundColor: "#000",
                "a:hover &": { backgroundColor: "#27272A" },
              }}
            >
              <Icon name="arrow-right" w={9.95196} h={6.65986} />
            </Stack>
          </Stack>
        </Box>
      )}
    </Box>
  );
});
